import { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { ArrowLeft, MapPin, ShieldCheck, Tag } from 'lucide-react'
import { Button } from '@cannect/new-components/atoms'
import FlavorSelect from '@cannect/new-components/FlavorSelect/FlavorSelect'
import { TRecipeProducts } from '@cannect/services/resources/recipeAccess'
import { formatRecipeDateTime } from '../formatRecipeDate'
import { useRecipeBrandColors } from '../useRecipeBrandColors'
import RecipeProduct from '../RecipeProduct'

type RecipeProductsScreenProps = {
  products: TRecipeProducts
  token: string
  emission: string
  prescriberName?: string
  onContinue: () => void
  onBack: () => void
  isContinueLoading?: boolean
}

const formatBrl = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)

// Datas de calendário (sem hora) de `delivery` — NÃO é `recipe.emission`
// (timestamp, usa `formatRecipeDateTime`). Aplicar fuso aqui criaria o bug
// oposto: "2026-10-25" vira meia-noite UTC e viraria 24 em Brasília.
const formatDeliveryDate = (iso: string) => {
  if (!iso) return ''
  const date = new Date(`${iso}T00:00:00`)
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('pt-BR')
}

// Achado do Lince (revisão do breakdown_consistent v1): o valor do frete
// vazava aqui mesmo com a quebra escondida — `totals.shipping` é um dos
// números que não fecham quando `breakdown_consistent === false`. Sem
// `no-nested-ternary`.
function getShippingCaption(
  noImportCost: boolean,
  isBreakdownConsistent: boolean,
  shipping: number,
  formatBrlFn: (value: number) => string
) {
  if (noImportCost) return 'Sem custo de importação para o seu CEP'
  if (!isBreakdownConsistent) return 'Calculado para o seu CEP'
  return `Custo de importação ${formatBrlFn(shipping)} · calculado para o seu CEP`
}

// D4/B5 do protótipo (`Desktop-B-produtos.dc.html`, `B-Produtos.dc.html`).
// "Alterar" CEP e "Cupom de desconto" NÃO chamam PUT /order_cart_public
// (decisão 37, pagamento é domínio regulado): os dois levam ao carrinho-wa
// pelo MESMO caminho do "Continuar" (POST order_recipe/:token -> linkCart),
// onde CEP e cupom já existem de verdade (NewCart/pageCartProducts.tsx:199-
// 208 cota CEP; NewCart/Resume/useResume.ts:40 aplica cupom via
// /coupon_public/valid-items) — não dupliquei esses fluxos aqui.
export const RecipeProductsScreen = ({
  products,
  token,
  emission,
  prescriberName,
  onContinue,
  onBack,
  isContinueLoading
}: RecipeProductsScreenProps) => {
  const history = useHistory()
  const colors = useRecipeBrandColors()
  const [selectedFlavors, setSelectedFlavors] = useState<Record<number, number | undefined>>({})

  if (!products.available) {
    return (
      <div className="flex w-full max-w-[420px] flex-col items-center gap-4 self-center rounded-2xl border border-gray-100 bg-white px-6 py-8 text-center shadow-sm">
        <h1 className="text-lg font-semibold text-gray-950">Nenhum produto disponível</h1>
        <p className="text-sm text-gray-500">
          Não encontramos produtos disponíveis para esta receita no momento. Fale com um atendente para mais
          informações.
        </p>
        <a
          href="https://whts.co/Cannect"
          className="w-full rounded-lg bg-primary-600 px-4 py-3 text-sm font-semibold text-white hover:bg-primary-700">
          Fale com um atendente
        </a>
      </div>
    )
  }

  const {
    items,
    totals,
    delivery,
    has_unavailable_items: hasUnavailableItems,
    unavailable_items: unavailableItems
  } = products
  // Decisão 26 (Gerente): não existe valor monetário de "custo de
  // importação" no sistema — o campo `shipping` É esse custo nesta tela.
  // Decisão 56: `products_subtotal` é o campo certo pra linha "Produtos"
  // (soma antes do desconto); `totals.subtotal` numa order real veio igual
  // a `amount` (achado do humano, decisão 54) — SEM fallback pra ele: na
  // ausência, a linha some em vez de mostrar um número errado.
  // Sugestão do Lince: checagem explícita de nulo/undefined, não de
  // verdade/falsidade — "0.00" (receita sem custo nos produtos) é um valor
  // válido, não "ausente".
  const productsSubtotal = totals.products_subtotal != null ? Number.parseFloat(totals.products_subtotal) : null
  // Decisão 59: só esconde a quebra quando o backend confirma que ela NÃO
  // soma pro Total (`false` explícito) — ausente/`true` segue mostrando,
  // pra não quebrar com um backend antigo que ainda não manda o campo.
  const isBreakdownConsistent = totals.breakdown_consistent !== false

  // Decisão rodada 2 (Gerente, nota [CC][F-carrinho-wa-novo-padrao]): só o estilo do
  // /carrinho (OrderSummary.tsx/Sumary.tsx) é copiado aqui — NÃO os componentes, que
  // recalculam preço no cliente. Nossos valores continuam vindo de `totals` (decisões
  // 10/54/56). Cor do Resumo = `primary-*` para todas as marcas (correção da leitura do
  // Esquadro: `primary-*` é o que os temas whitelabel recolorem, não quebra nada).
  const summary = (
    <div className="flex w-full flex-col gap-3.5 rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
      <h2 className="text-lg font-semibold text-gray-900">Resumo do pedido</h2>

      <div className="flex flex-col gap-1 border-b border-gray-100 pb-3.5 text-sm text-gray-600">
        {/* Achado 51: quando falta data de início/fim, NÃO renderizar "entre  e " quebrado —
            sem inventar prazo, só o complemento de custo/CEP. */}
        {delivery.estimated_delivery_range_start && delivery.estimated_delivery_range_end && (
          <p className="font-medium text-gray-900">
            Entrega estimada entre {formatDeliveryDate(delivery.estimated_delivery_range_start)} e{' '}
            {formatDeliveryDate(delivery.estimated_delivery_range_end)}
          </p>
        )}
        <p className="text-gray-500">
          {getShippingCaption(totals.no_import_cost, isBreakdownConsistent, totals.shipping, formatBrl)}
        </p>
        {/* Mesmo campo de CEP de OrderSummary.tsx (NewCartCOD/v2): fundo secondary,
            ícone MapPin e botão à direita. Sem PUT aqui (decisão 37) — Validar/Alterar
            segue para o carrinho-wa, onde o CEP de fato é aplicado. */}
        <div className="relative pt-1">
          <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            readOnly
            placeholder="Digite seu CEP:"
            value={delivery.cep || ''}
            className="w-[calc(100%-90px)] rounded-md border-0 bg-[#EDECE7] py-2 pl-10 pr-3 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none"
          />
          <div className="absolute right-0 top-1/2 -translate-y-1/2">
            <button
              type="button"
              onClick={onContinue}
              className="rounded-md border-none bg-primary-700 px-3 py-2 text-sm text-white hover:bg-primary-800">
              {delivery.cep ? 'Alterar' : 'Validar'}
            </button>
          </div>
        </div>
      </div>

      {/* Decisão 49 (Esquadro 5/5): protótipo mobile não tem o card de cupom — só desktop.
          Decisão 6 (rodada 2): estilo de CouponSection.tsx, mas continua levando ao
          carrinho (decisão 37) — aplicar cupom inline exige o contexto de carrinho, que a
          receita não tem. */}
      <div className="relative hidden lg:block">
        <Tag className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <button
          type="button"
          onClick={onContinue}
          className="w-full rounded-md border-0 bg-[#EDECE7] py-2 pl-10 pr-20 text-left text-sm text-gray-500">
          Tem um cupom?
        </button>
        <div className="absolute right-0 top-1/2 -translate-y-1/2">
          <button
            type="button"
            onClick={onContinue}
            className="rounded-md border-none bg-primary-700 px-3 py-2 text-sm text-white hover:bg-primary-800">
            Aplicar
          </button>
        </div>
      </div>

      {/* Decisão 49: a quebra Produtos/Desconto/Importação some abaixo de `lg` — o protótipo
          mobile só mostra Entrega+CEP+banner+rodapé fixo (Total/Pix/Continuar).
          Decisão 59: também some em QUALQUER largura se `breakdown_consistent === false` —
          uma conta que não fecha é pior que nenhuma conta. */}
      {isBreakdownConsistent && (
        <div className="hidden flex-col gap-2 lg:flex">
          {productsSubtotal !== null && (
            <div className="flex justify-between text-sm text-gray-600">
              <span>Produtos</span>
              <span className="text-gray-900">{formatBrl(productsSubtotal)}</span>
            </div>
          )}
          {totals.discount > 0 && (
            <div className="flex justify-between text-sm text-gray-600">
              <span>Desconto nos produtos</span>
              <span className="font-semibold text-primary-800">-{formatBrl(totals.discount)}</span>
            </div>
          )}
          {!totals.no_import_cost && (
            <div className="flex justify-between text-sm text-gray-600">
              <span>Custo de importação</span>
              <span className="text-gray-900">{formatBrl(totals.shipping)}</span>
            </div>
          )}
          {totals.shipping_discount > 0 && (
            <div className="flex justify-between text-sm text-gray-600">
              <span>Desconto na importação</span>
              <span className="text-gray-900">-{formatBrl(totals.shipping_discount)}</span>
            </div>
          )}
        </div>
      )}

      <div className="hidden items-baseline justify-between border-t border-gray-200 pt-3 lg:flex">
        <span className="text-base font-normal text-gray-800">Total</span>
        <span className="text-lg font-semibold text-gray-900">{formatBrl(totals.amount)}</span>
      </div>

      {/* Decisão 2 (rodada 2): sem selo — linha verde no estilo de Sumary.tsx:140-145. O
          valor e a regra (informativo, vindo do backend) não mudam. */}
      {totals.pix.discount_enabled && (
        <div className="hidden items-center justify-between text-sm text-green-600 lg:flex">
          <span>No Pix você paga</span>
          <b>
            {formatBrl(totals.pix.amount)}{' '}
            <span className="font-medium">(-{formatBrl(totals.amount - totals.pix.amount)})</span>
          </b>
        </div>
      )}

      {/* Decisão 3 (rodada 2): 8px reto (igual a /carrinho) vale só para o Continuar do
          Resumo — os outros botões do fluxo seguem o protótipo aprovado (pill). */}
      <Button
        isLoading={isContinueLoading}
        onClick={onContinue}
        className="hidden h-10 w-full rounded bg-primary-800 text-base font-semibold text-white hover:bg-primary-900 lg:flex">
        Continuar
      </Button>
    </div>
  )

  return (
    <div className="flex w-full flex-col gap-5 pb-24 lg:max-w-[1120px] lg:pb-0">
      <div>
        <div className="flex items-center justify-between gap-3">
          <Button
            unstyled
            onClick={onBack}
            aria-label="Voltar à receita"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-100">
            <ArrowLeft size={18} />
          </Button>
          <Button
            unstyled
            onClick={() => history.push(`/receitas/${token}/ajuda`)}
            className={`text-[13px] font-semibold ${colors.link}`}>
            Precisa de ajuda?
          </Button>
        </div>
        <h1 className="mt-2 text-2xl font-semibold text-gray-950 lg:text-[30px]">Produtos da sua receita</h1>
        <p className="mt-1.5 text-sm text-gray-500">
          Receita de {formatRecipeDateTime(emission)}
          {prescriberName ? ` · Dr(a). ${prescriberName}` : ''}
        </p>
      </div>

      <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
        <div className="flex min-w-0 flex-grow flex-col gap-5">
          <h2 className="text-[18px] font-semibold text-gray-950">Seu pedido</h2>

          <div className="flex flex-col gap-3">
            {items.map((item) => (
              <div key={item.id} className="rounded-2xl border border-gray-100 bg-white">
                <RecipeProduct
                  product={item}
                  flavorSlot={
                    <div className="mt-2 flex">
                      <FlavorSelect
                        productId={item.product.id}
                        value={selectedFlavors[item.product.id]}
                        onChange={(flavorId) =>
                          setSelectedFlavors((prev) => ({ ...prev, [item.product.id]: flavorId }))
                        }
                      />
                    </div>
                  }
                />
              </div>
            ))}

            {hasUnavailableItems &&
              unavailableItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between gap-2 rounded-xl border border-dashed border-gray-200 bg-gray-50 p-3 opacity-70">
                  <p className="text-sm font-medium text-gray-700">{item.product.translated_name}</p>
                  <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-600">
                    Indisponível
                  </span>
                </div>
              ))}
          </div>

          {!totals.no_import_cost && (
            <div
              className={`flex items-start gap-2.5 rounded-xl p-3.5 text-[12.5px] leading-relaxed ${colors.softBg} ${colors.darkText}`}>
              <ShieldCheck size={18} className="mt-0.5 shrink-0" />
              <p>
                <b>Produto importado.</b> Depois do pagamento, pedimos seus documentos. Se quiser, a Cannect cuida da
                autorização na Anvisa para você, sem custo.
              </p>
            </div>
          )}
        </div>

        <aside className="w-full lg:w-[380px] lg:shrink-0">{summary}</aside>
      </div>

      {/* Mobile: /carrinho não tem rodapé fixo nem botão próprio aqui (só sidebar sticky no
          desktop, pageCartProductsV2.tsx:355) — mantemos o rodapé fixo atual (decisão 5,
          rodada 2), com os tokens do Resumo. É o MESMO Continuar do Resumo (card escondido
          no mobile) — correção (Gerente, rodada 3): também em 8px, não pill. */}
      <div className="fixed inset-x-0 bottom-0 flex items-center gap-3.5 border-t border-gray-100 bg-white px-5 py-3 lg:hidden">
        <div className="flex shrink-0 flex-col">
          <span className="text-xs text-gray-500">Total</span>
          <b className="text-lg text-gray-950">{formatBrl(totals.amount)}</b>
          {totals.pix.discount_enabled && (
            <span className="text-xs font-semibold text-primary-800">{formatBrl(totals.pix.amount)} no Pix</span>
          )}
        </div>
        <Button
          isLoading={isContinueLoading}
          onClick={onContinue}
          className="h-10 flex-grow rounded bg-primary-800 text-base font-semibold text-white hover:bg-primary-900">
          Continuar
        </Button>
      </div>
    </div>
  )
}
